import {
  createSlice,
  createSelector,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "main/features/AxiosWrapper";
import { ErrorResponse } from "main/types/errorResponse";
import { API_BASE_PATH } from "main/constants/constant";
import { MasterData } from "main/types/masterData";
import { RootState } from "main/store";
import { deepMergeRecursive } from "../utils/sliceUtil";

const key = "masterData";

const roomAmenityAdapter = createEntityAdapter<MasterData.RoomAmenity>();
const roomPropertyAdapter = createEntityAdapter<MasterData.RoomProperty>();
const planOptionAdapter = createEntityAdapter<MasterData.PlanOption>();
const masterFilterAdapter = createEntityAdapter<MasterData.MasterFilter>();
const countryAdapter = createEntityAdapter({
  selectId: (country: MasterData.Country) => country.value,
});
const japanPrefectureAdapter =
  createEntityAdapter<MasterData.JapanPrefecture>();

const roomPropertiesInitialStateValues: MasterData.RoomPropertyState = {
  errors: null,
  loading: false,
};
const roomAmenitiesInitialStateValues: MasterData.RoomAmenityState = {
  errors: null,
  loading: false,
};
const planOptionsInitialStateValues: MasterData.PlanOptionState = {
  errors: null,
  loading: false,
};
const masterFiltersInitialStateValues: MasterData.MasterFilterState = {
  errors: null,
  loading: false,
};
const countriesInitialStateValues: MasterData.CountryState = {
  errors: null,
  loading: false,
};
const japanPrefecturesInitialStateValues: MasterData.JapanPrefectureState = {
  errors: null,
  loading: false,
};

const initialState = {
  success: false,
  roomProperties: roomPropertyAdapter.getInitialState(
    roomPropertiesInitialStateValues,
  ),
  roomAmenities: roomAmenityAdapter.getInitialState(
    roomAmenitiesInitialStateValues,
  ),
  planOptions: planOptionAdapter.getInitialState(planOptionsInitialStateValues),
  masterFilters: masterFilterAdapter.getInitialState(
    masterFiltersInitialStateValues,
  ),
  countries: countryAdapter.getInitialState(countriesInitialStateValues),
  japanPrefectures: japanPrefectureAdapter.getInitialState(
    japanPrefecturesInitialStateValues,
  ),
};

/** Async **/
export const fetchMasterData = createAsyncThunk<
  {
    roomProperties: MasterData.RoomProperty[];
    roomAmenities: MasterData.RoomAmenity[];
    planOptions: MasterData.PlanOption[];
    masterFilters: MasterData.MasterFilter[];
    countries: MasterData.Country[];
    japanPrefectures: MasterData.JapanPrefecture[];
  },
  {
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchMasterData`, async (args, { rejectWithValue }) => {
  try {
    const { isServerSide = false, headers, locale } = args;
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/v1/master_data.json`,
      {
        headers,
      },
    );
    return result.data;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const masterDataSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        // masterDataはネスト構造のため再帰版deepMergeを使用する
        const masterData = deepMergeRecursive(state, action.payload.masterData);
        return { ...state, ...masterData };
      })
      .addCase(fetchMasterData.fulfilled, (state, action) => {
        roomAmenityAdapter.setAll(
          state.roomAmenities,
          action.payload.roomAmenities,
        );
        roomPropertyAdapter.setAll(
          state.roomProperties,
          action.payload.roomProperties,
        );
        planOptionAdapter.setAll(state.planOptions, action.payload.planOptions);
        masterFilterAdapter.setAll(
          state.masterFilters,
          action.payload.masterFilters,
        );
        countryAdapter.setAll(state.countries, action.payload.countries);
        if (action.payload.japanPrefectures) {
          japanPrefectureAdapter.setAll(
            state.japanPrefectures,
            action.payload.japanPrefectures,
          );
        }
      });
  },
});

/** selector * */
export const {
  // selectById: roomAmenityByIdSelector,
  selectAll: roomAmenitiesSelector,
} = roomAmenityAdapter.getSelectors(
  (state: RootState) => state.masterData.roomAmenities,
);

export const {
  // selectById: roomPropertyByIdSelector,
  selectAll: roomPropertiesSelector,
} = roomPropertyAdapter.getSelectors(
  (state: RootState) => state.masterData.roomProperties,
);

export const {
  // selectById: planOptionByIdSelector,
  selectAll: planOptionsSelector,
} = planOptionAdapter.getSelectors(
  (state: RootState) => state.masterData.planOptions,
);

export const { selectById: countryByIdSelector, selectAll: countriesSelector } =
  countryAdapter.getSelectors((state: RootState) => state.masterData.countries);

export const {
  selectById: japanPrefectureByIdSelector,
  selectAll: japanPrefecturesSelector,
} = japanPrefectureAdapter.getSelectors(
  (state: RootState) => state.masterData.japanPrefectures,
);

export const {
  // selectById: masterFilterByIdSelector,
  selectAll: masterFiltersSelector,
  selectEntities: masterFilterEntitiesSelector,
} = masterFilterAdapter.getSelectors(
  (state: RootState) => state.masterData.masterFilters,
);

// const stateSelector = (state: { [key]: IMasterDataState }) => state[key];

export const roomPropertiesBySelector = createSelector(
  [
    roomPropertiesSelector,
    (state, hotelRoomProperties: { id: number }[]) => hotelRoomProperties,
  ],
  (planOptions, hotelRoomProperties) => {
    const hotelPlanOptionIds = hotelRoomProperties.map((hotelRoomProperty) => {
      return hotelRoomProperty.id;
    });
    return planOptions.filter((planOption) => {
      return hotelPlanOptionIds.includes(planOption.id);
    });
  },
);

export const roomAmenitiesBySelector = createSelector(
  [
    roomAmenitiesSelector,
    (state, hotelRoomAmenities: { id: number }[]) => hotelRoomAmenities,
  ],
  (planOptions, hotelRoomAmenities) => {
    const hotelPlanOptionIds = hotelRoomAmenities.map((hotelRoomAmenity) => {
      return hotelRoomAmenity.id;
    });
    return planOptions.filter((planOption) => {
      return hotelPlanOptionIds.includes(planOption.id);
    });
  },
);

export const planOptionsBySelector = createSelector(
  [
    planOptionsSelector,
    (state, hotelPlanOptions: { id: number }[]) => hotelPlanOptions,
  ],
  (planOptions, hotelPlanOptions) => {
    const hotelPlanOptionIds = hotelPlanOptions.map((hotelPlanOption) => {
      return hotelPlanOption.id;
    });
    return planOptions.filter((planOption) => {
      return hotelPlanOptionIds.includes(planOption.id);
    });
  },
);

export const masterFiltersBySelector = createSelector(
  [masterFilterEntitiesSelector, (state, filterIds: number[]) => filterIds],
  (masterFilterEntities, filterIds) => {
    // filterId順に取得する
    return filterIds.map((id) => {
      return masterFilterEntities[id];
    });
  },
);
